import * as React from "react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  Path,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete, {
  AutocompleteRenderInputParams
} from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";
import { get } from "lodash";

import { useAppSelector } from "app/hooks";
import InventoryProductField from "components/inventory-product-field";
import {
  categoryGroupOf,
  sortByCategory
} from "components/product-category-filter";
import {
  INVENTORY_PRODUCT_STATUS_IN_STOCK,
  INVENTORY_PRODUCT_STATUS_RECEIVED
} from "constants/inventory-products";
import { PRODUCT_SUB_TYPES } from "constants/products";
import { PRODUCT_TYPES } from "constants/sales";
import { selectCurrentClinic } from "features/auth/auth-slice";
import useProductCategories from "hooks/use-product-categories.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import {
  useGetProductCatalogOptionsQuery,
  useGetProductsQuery
} from "services/products";
import {
  PRODUCT_CATALOG_QUERY,
  PRODUCT_QUERY,
  useGetSearchProductCatalogOptionsQuery,
  withProductCategory
} from "services/products-search";
import {
  InventoryProduct,
  InventoryProductsFilters
} from "services/types/inventory-products.type";
import {
  ProductCatalogOption,
  ProductsFilters
} from "services/types/products.type";
import { formatInventoryProductToProductCatalogOption } from "utils/products.utils";

import CategoryFilter from "./components/category-filter.component";
import GroupedListItems from "./components/grouped-list-items.component";
import NewProductAdditionModal from "./components/new-product-addition-modal.component";
import ProductListItem from "./components/product-list-item.component";
import { formatOptionsWithSelectedProducts } from "./product-catalog-field.util";
import { useHideFromStock } from "./product-catalog-hide-from-stock-context";
import useCatalogProducts from "./use-catalog-products.hook";
import useProductCatalogField, {
  useCategoryFilter
} from "./use-product-catalog-field.hook";

// Synthetic option id for the appended "Add New ..." row. Not a real product.
export const ADD_NEW_PRODUCT_OPTION_ID = -1;

export const NO_CATALOG_PERMISSION_TOOLTIP =
  "You don't have permission to add new catalog items. Contact your administrator.";

type ProductCatalogFieldType<T extends FieldValues> = {
  allowInventoryStock?: boolean;
  required?: boolean;
  type?: number;
  subtype?: number | number[];
  ear?: "L" | "R" | "";
  label: string;
  placeholder?: string;
  autofocus?: boolean;
  icon?: ReactNode;
  sx?: SxProps;
  initialSearch?: string;
  clearAfterSelect?: boolean;
  showComponentPrice?: boolean;
  onProductSelected?: (product: ProductCatalogOption) => void;
  formatInventoryProductOptions?: (
    inventoryProducts: InventoryProduct[]
  ) => InventoryProduct[];
  disabled?: boolean;
  filters?: Partial<ProductsFilters>;
  inventoryProductFilters?: Partial<InventoryProductsFilters>;
  // Which clinics the "From stock" picker reads from (SPA-6232): a single id,
  // a list of ids, or [] for the whole practice. Omitted → the current clinic,
  // which is every caller's behavior before this prop existed. Pair with
  // `showStockClinicInfo` when the resolved scope spans more than one clinic.
  stockClinicIds?: number | number[];
  showStockClinicInfo?: boolean;
  rowSpacing?: number;
  columnSpacing?: number;
  spacing?: number;
  allowNewProductAddition?: boolean;
  // The type/subtype the "Add New ..." row creates under, when it differs from
  // the type/subtype the dropdown SEARCHES by. The repair-service picker needs
  // this: it searches with a `repair_selectable` filter and passes no
  // type/subtype at all, but must still create a Service / Repair product.
  // Both fall back to `type` / `subtype` when omitted.
  newProductType?: number;
  newProductSubtype?: number | number[];
  // Overrides the noun in the "Add New <noun>" row and the create modal title.
  // Defaults to the subtype (or type) label.
  newProductLabel?: string;
  allowNonStock?: boolean;
  hideProductSearch?: boolean;
  // Lock the catalog product dropdown while keeping "From stock" active — used
  // by the quote conversion flow where the device is fixed by the quote and
  // only its sourcing (stock unit vs order) can be chosen (SN-18215)
  disableProductSearch?: boolean;
  // Show clinic-stock tags read-only in the "From stock" options (flag-gated in the field).
  showAssignedTags?: boolean;
} & UseControllerProps<T>;

export default function ProductCatalogField<T extends FieldValues>({
  control,
  name,
  required = false,
  label,
  placeholder,
  type,
  subtype,
  ear,
  initialSearch = "",
  icon,
  autofocus = false,
  sx = {},
  clearAfterSelect = false,
  allowInventoryStock = false,
  onProductSelected,
  showComponentPrice = false,
  allowNewProductAddition = false,
  newProductType,
  newProductSubtype,
  newProductLabel,
  disabled,
  filters = { is_active: true },
  inventoryProductFilters = {},
  formatInventoryProductOptions,
  stockClinicIds,
  showStockClinicInfo = false,
  rowSpacing = 2,
  columnSpacing = 0,
  spacing = 0,
  hideProductSearch,
  disableProductSearch = false,
  allowNonStock = false,
  showAssignedTags = false
}: ProductCatalogFieldType<T>) {
  const {
    getIsProductSelectedInfo,
    rememberSelectedProduct,
    forgetSelectedProduct
  } = useProductCatalogField();
  // Matches the gate on the Catalog page's "Add New" button: admin-level groups
  // OR the per-user `can_edit_catalog` flag. Previously this was
  // `isAdminOrBusinessOwner`, which silently excluded staff who had been
  // granted catalog editing explicitly.
  const { canEditCatalog } = useUserRoles();
  // Quotes are estimates and must not reserve clinic inventory, so the wizard
  // hides the "From stock" picker while building a quote (SN-18215).
  const hideFromStock = useHideFromStock();
  const [showNewProductAdditionForm, setShowNewProductAdditionForm] =
    useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [productType, setProductType] = useState(type);
  const [productSubtype, setProductSubtype] = useState(subtype);
  const [search, setSearch] = useState<string>(initialSearch);
  const [options, setOptions] = useState<ProductCatalogOption[]>([]);
  const [open, setOpen] = useState<boolean>(autofocus);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const {
    isEnabled: productCategories,
    categories,
    activeCategories,
    getCategory
  } = useProductCategories();
  const showCategoryFilter = productCategories && activeCategories.length > 0;
  const [categoryFilter, setCategoryFilter] = useCategoryFilter(String(type));
  const categoryParam =
    showCategoryFilter && categoryFilter !== "" ? [categoryFilter] : undefined;
  const { value: dSearch, isDebouncing } = useDebounce(search);
  const validate = useCallback(
    (value: any) =>
      !required ||
      (required && value !== null && value !== undefined && value !== "")
        ? true
        : `${label || "Value"} is required`,
    [required, label]
  );
  const {
    field: { onChange, onBlur, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: { validate }
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const [fieldValue, stockFieldValue] = useWatch<T, [typeof name, Path<T>]>({
    control,
    name: [name, `${name}_from_stock` as Path<T>]
  });

  const renderInput = useCallback(
    ({ InputProps, ...restParams }: AutocompleteRenderInputParams) => (
      <TextField
        {...restParams}
        InputProps={{
          ...InputProps,
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined
        }}
        sx={sx}
        InputLabelProps={{ disabled: disabled }}
        required={required}
        error={Boolean(errorMessage)}
        helperText={errorMessage || ""}
        label={label}
        autoFocus={open}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [errorMessage, open, sx, icon, placeholder, label, required, disabled]
  );

  const {
    data: products,
    isLoading,
    isSuccess,
    refetch
  } = useCatalogProducts<ProductsFilters, ProductCatalogOption>({
    useQuery: allowInventoryStock
      ? Boolean(dSearch)
        ? useGetSearchProductCatalogOptionsQuery
        : useGetProductCatalogOptionsQuery
      : useGetProductsQuery,
    params: {
      ...filters,
      search: allowInventoryStock ? dSearch : undefined,
      display_name: allowInventoryStock ? undefined : dSearch,
      type: productType,
      subtype: productSubtype,
      threshold: allowInventoryStock && Boolean(fieldValue) ? 0.5 : undefined,
      query: withProductCategory(
        allowInventoryStock ? PRODUCT_CATALOG_QUERY : PRODUCT_QUERY,
        productCategories
      ),
      category: categoryParam
    }
  });

  // With every category showing, the list is grouped under category headings so
  // same-name products sit in labelled sections.
  const groupByCategory =
    showCategoryFilter &&
    categoryFilter === "" &&
    (products || []).some((product) => product.category != null);
  const chosenCategory =
    categoryFilter === "" ? undefined : getCategory(categoryFilter);
  const categoryIsEmpty =
    Boolean(chosenCategory) && isSuccess && !dSearch && !products?.length;

  useEffect(() => {
    if (type !== productType) {
      setProductType(type);
    }
    if (subtype !== productSubtype) {
      setProductSubtype(subtype);
    }
  }, [type, subtype, productType, productSubtype]);

  useEffect(() => {
    if (Boolean(fieldValue)) {
      setSearch(fieldValue.display_name ?? fieldValue.name);
    } else {
      setSearch("");
    }
  }, [fieldValue]);

  // The "Add New ..." row creates under `newProductType`/`newProductSubtype`
  // when given, falling back to whatever the dropdown searches by.
  const addNewType = newProductType ?? type;
  const addNewSubtype = newProductSubtype ?? subtype;

  const addNewNoun = useMemo(() => {
    if (newProductLabel) {
      return newProductLabel;
    }
    return addNewSubtype && !Array.isArray(addNewSubtype)
      ? PRODUCT_SUB_TYPES.getFromCode(addNewSubtype)
      : PRODUCT_TYPES.getFromCode(addNewType);
  }, [newProductLabel, addNewSubtype, addNewType]);

  useEffect(() => {
    let productOptions = groupByCategory
      ? sortByCategory(products || [], categories)
      : products || [];
    // The row is appended for every caller that opts in — including users who
    // cannot edit the catalog, who get it greyed out with a tooltip rather than
    // not knowing the capability exists at all.
    if (allowNewProductAddition) {
      productOptions = [
        ...productOptions,
        {
          id: ADD_NEW_PRODUCT_OPTION_ID,
          master_product: {},
          display_name: `Add ${
            search ? `"${search}" as ` : ""
          }New ${addNewNoun}`,
          add_new_disabled_reason: canEditCatalog
            ? undefined
            : NO_CATALOG_PERMISSION_TOOLTIP
        } as ProductCatalogOption
      ];
    }

    setOptions(productOptions);
  }, [
    allowNewProductAddition,
    products,
    setOptions,
    canEditCatalog,
    addNewNoun,
    search,
    groupByCategory,
    categories
  ]);

  const formatInventoryProductsOptions = useCallback(
    (inventoryProducts: InventoryProduct[]) => {
      const inventoryProductsOptions = getIsProductSelectedInfo
        ? formatOptionsWithSelectedProducts(
            inventoryProducts,
            name,
            getIsProductSelectedInfo
          )
        : inventoryProducts;
      return formatInventoryProductOptions
        ? formatInventoryProductOptions(inventoryProductsOptions)
        : inventoryProductsOptions;
    },
    [getIsProductSelectedInfo, name, formatInventoryProductOptions]
  );

  const handleCloseNewProductAdditionForm = useCallback(() => {
    setShowNewProductAdditionForm(false);
  }, []);

  const handleSaveNewProductAdditionForm = useCallback(
    async (product: ProductCatalogOption) => {
      refetch();
      onChange(product);
      onProductSelected?.(product);
      setSearch(product?.display_name ?? product?.name);
      // Opening the create modal blurs the input, so a form on `mode: "onBlur"`
      // (the repair dialog) has already painted "required" on this field by the
      // time we get here. `onChange` alone does NOT revalidate in that mode
      // until the form has been submitted once, so the error would sit there
      // over a field that is now filled in. Mirror the normal selection path,
      // which ends in `onBlur()` for exactly this reason.
      onBlur();

      handleCloseNewProductAdditionForm();
    },
    [
      handleCloseNewProductAdditionForm,
      onChange,
      onBlur,
      onProductSelected,
      refetch
    ]
  );

  if (!(allowInventoryStock || isSuccess)) {
    return <></>;
  }

  return (
    <Grid container rowSpacing={rowSpacing} columnSpacing={columnSpacing}>
      {showCategoryFilter && !hideProductSearch && (
        <Grid item xs={12} sx={{ mb: spacing }}>
          <CategoryFilter
            categories={activeCategories}
            value={categoryFilter}
            onChange={setCategoryFilter}
            disabled={disabled || disableProductSearch}
          />
          {categoryIsEmpty && chosenCategory && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="empty-category"
              sx={{ mt: 1 }}
            >
              No products in {chosenCategory.name}.{" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setCategoryFilter("")}
              >
                Show all categories
              </Link>
            </Typography>
          )}
        </Grid>
      )}
      {allowInventoryStock && !hideFromStock && (
        <Grid item xs={12} sx={{ mb: spacing }}>
          <InventoryProductField
            type={type as number}
            subtype={subtype}
            label="From stock"
            control={control}
            product={restField.value}
            allowInventoryProductAddition
            allowNonStock={allowNonStock}
            showAssignedTags={showAssignedTags}
            showClinicGroups={showStockClinicInfo}
            name={`${name}_from_stock` as Path<T>}
            disabled={disabled}
            filters={{
              // Always send the key — InventoryProductField spreads these over
              // its own count args, so an omitted `clinic` would leave list and
              // count filtering different populations.
              clinic: stockClinicIds ?? currentClinic?.id,
              ear: ear || undefined,
              category: categoryParam,
              status: [
                INVENTORY_PRODUCT_STATUS_IN_STOCK,
                INVENTORY_PRODUCT_STATUS_RECEIVED
              ],
              ...inventoryProductFilters,
              baseFilters: {
                is_clinic_stock: true,
                is_inventory: true,
                is_active: true,
                ...inventoryProductFilters?.baseFilters
              }
            }}
            clearAfterSelect={clearAfterSelect}
            onInventoryProductSelected={(
              inventoryProduct: InventoryProduct | null | undefined
            ) => {
              const catalogProduct =
                formatInventoryProductToProductCatalogOption(inventoryProduct);
              if (rememberSelectedProduct && !catalogProduct && fieldValue) {
                forgetSelectedProduct(name);
              } else if (forgetSelectedProduct && catalogProduct) {
                rememberSelectedProduct(catalogProduct as any, name);
              }
              if (Boolean(catalogProduct)) {
                onChange(catalogProduct);
                setSearch(
                  (catalogProduct as ProductCatalogOption).display_name || ""
                );
                if (onProductSelected) {
                  onProductSelected(catalogProduct as ProductCatalogOption);
                  if (clearAfterSelect) {
                    setSearch("");
                  }
                }
              }
              onBlur();
            }}
            formatOptions={formatInventoryProductsOptions}
          />
        </Grid>
      )}
      {!hideProductSearch && (
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete
              disablePortal
              loading={isLoading || isDebouncing}
              fullWidth
              sx={{ flexGrow: 1 }}
              disabled={
                disabled || disableProductSearch || Boolean(stockFieldValue)
              }
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              open={open}
              options={options}
              renderGroup={GroupedListItems}
              groupBy={
                groupByCategory
                  ? (option: ProductCatalogOption) =>
                      categoryGroupOf(
                        option,
                        getCategory,
                        option.id !== ADD_NEW_PRODUCT_OPTION_ID
                      )
                  : undefined
              }
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : (option?.display_name ?? option.name)
              }
              renderOption={(props: object, option: ProductCatalogOption) => (
                <ProductListItem
                  props={props}
                  option={option}
                  key={`${option?.id}-${option?.inventory_id}-${option?.serial_number}`}
                  showComponentPrice={showComponentPrice}
                />
              )}
              isOptionEqualToValue={(
                option: ProductCatalogOption,
                value: ProductCatalogOption
              ) =>
                option?.id === value?.id &&
                option?.inventory_id === value?.inventory_id
              }
              inputValue={search}
              onInputChange={(event, newInputValue, reason) => {
                if (Boolean(event)) {
                  if (reason === "input" || reason === "reset") {
                    setSearch(newInputValue);
                  } else {
                    setSearch("");
                  }
                }
              }}
              filterOptions={(x) => x}
              getOptionDisabled={(option: ProductCatalogOption) =>
                Boolean(option?.add_new_disabled_reason)
              }
              popupIcon={<ArrowDropDownIcon />}
              renderInput={renderInput}
              onChange={(_, data) => {
                if (
                  !Array.isArray(data) &&
                  data?.id === ADD_NEW_PRODUCT_OPTION_ID
                ) {
                  // Belt-and-braces alongside getOptionDisabled: never open the
                  // create modal for a user who cannot edit the catalog.
                  if (data?.add_new_disabled_reason) {
                    return;
                  }
                  setNewProductName(search);
                  setShowNewProductAdditionForm(true);
                  return;
                } else {
                  setShowNewProductAdditionForm(false);
                }
                onChange(data);
                if (Boolean(data)) {
                  setSearch(
                    (data as ProductCatalogOption).display_name ??
                      ((data as ProductCatalogOption).name || "")
                  );
                }
                if (onProductSelected) {
                  onProductSelected(data as ProductCatalogOption);
                  if (clearAfterSelect) {
                    setSearch("");
                  }
                }
                onBlur();
              }}
              onBlur={onBlur}
              {...restField}
            />
          </Stack>
          {allowNewProductAddition && canEditCatalog && (
            <NewProductAdditionModal
              type={addNewType as number}
              subtype={addNewSubtype}
              title={newProductLabel ? `Add New ${newProductLabel}` : undefined}
              name={newProductName}
              isOpen={showNewProductAdditionForm}
              onCancel={handleCloseNewProductAdditionForm}
              onSave={handleSaveNewProductAdditionForm}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
}
